/**
 * The **structured note** — what a capture becomes when something reads it (APP-10, ADR 0127).
 *
 * Shared because three parties parse it: the gateway parses what the model returned before it will
 * hand it to anyone, the device parses what the gateway returned, and the device parses it again
 * on the way back out of storage. Services never import each other (ADR 0001), so a shape that two
 * of them must agree on lives here or it is written twice and drifts.
 *
 * ## The rule this file exists to hold
 *
 * **A structure is DERIVED. It never replaces what was said.** `MultiCaptureWireSchema` keeps
 * `title` and `body` exactly as they are, required, and adds this beside them. Every bound below
 * is chosen so that a model which ignores its instructions and echoes the transcript back cannot
 * land that echo in a field the card renders *instead of* the raw text — it fails to parse, the
 * device records the failure, and the card falls back to what it shows today.
 */
import { z } from 'zod';

import { ModelSelectionSchema } from './v1.js';

/**
 * How long one line of a structured note may be.
 *
 * These are not arbitrary. `summary` is what the card shows in place of the transcript, so it is
 * bounded well below the 20,000 characters `body` allows: a "summary" that could hold the whole
 * capture is not a summary, and the failure of the thing that generates it is *verbosity*. The
 * ceiling turns that failure into a parse error somebody is told about rather than a card that
 * silently re-renders the dictation.
 */
const TITLE_MAX = 200;
const SUMMARY_MAX = 1_200;
const LINE_MAX = 400;
/** Twelve of anything is already more than a phone card can show. Past it, something is wrong. */
const LIST_MAX = 12;

/**
 * One structured reading of one capture.
 *
 * Every list may be EMPTY, and that is the common case rather than a degenerate one: most captures
 * are a single thought with no key points and nothing to do. Requiring a non-empty `points` would
 * push whatever fills this in towards inventing one — a card confidently listing something the
 * operator never said, which is worse than a card with nothing under the summary.
 */
export const NoteStructureSchema = z.object({
  /**
   * A real title, as opposed to `Capture.title`, which is the first characters of the transcript.
   *
   * The one field with no honest empty rendering — every other field degrades to "nothing to show
   * here", and an absent title degrades to a nameless card — so it is the one field required to be
   * non-empty.
   */
  title: z.string().min(1).max(TITLE_MAX),
  /** What the note says, in a line or two. Shown on the card in place of the raw transcript. */
  summary: z.string().max(SUMMARY_MAX),
  /** The points worth keeping, in the note's own order. */
  points: z.array(z.string().min(1).max(LINE_MAX)).max(LIST_MAX),
  /**
   * The things the operator said they would DO — the whole input to the TASKS column.
   *
   * Text only, deliberately. A due date is the obvious next field and it is not here: nothing in
   * the capture path records one, so a date on this shape could only be guessed, and a task that
   * claims a deadline nobody set is worse than one with none (`AF-MULTI72`).
   */
  actions: z.array(z.string().min(1).max(LINE_MAX)).max(LIST_MAX),
  /** Short topic words, for later grouping. Not a notebook and not a type — those are the operator's. */
  tags: z.array(z.string().min(1).max(40)).max(LIST_MAX),
  /**
   * The language the structure is WRITTEN IN, as a bare subtag (`en`, `ar`) or `''` when unknown.
   *
   * Not a claim about the language the operator spoke — Multi has never guessed that and does not
   * start here (`core/voice-view.ts`). It is what the structurer produced, which is the only thing
   * that can be known without asking, and it is what a voice would have to be chosen against.
   */
  language: z.string().max(16),
  /**
   * WHAT produced this. `heuristic` for the deterministic structurer, otherwise the model id.
   *
   * On the record rather than in a log, because a structure is shown to a person as though it were
   * a reading of their note, and "which thing read it" is part of being able to trust it. It is
   * also what makes a silently-degraded deploy visible from the data rather than only from health.
   */
  model: z.string().min(1).max(120),
  /** When it was produced. Server time — the gateway stamps it from its own injected clock. */
  structuredAt: z.number().int().nonnegative(),
});
export type NoteStructure = z.infer<typeof NoteStructureSchema>;

/**
 * `POST /api/multi/v1/structure` — read this note and give me its shape back.
 *
 * The owner is deliberately NOT a field. It comes from the verified Access identity and from
 * nowhere else (ADR 0117 §4); a request field for it is a value the server must ignore, and a
 * field the server ignores is one that eventually gets trusted by accident.
 */
export const MultiStructureRequestSchema = z.object({
  /** The capture this is about. Echoed back, so a late response can be matched to its card. */
  id: z.string().min(1).max(64),
  /** The operator's own label. An input to the structurer, never a thing it may change. */
  type: z.string().min(1).max(32),
  /** What was actually said. Trimmed-empty is refused: there is nothing to structure. */
  text: z.string().trim().min(1).max(20_000),
  /**
   * WHICH MODEL SHOULD READ IT — the operator's choice, when they have made one (APP-11).
   *
   * Optional, and absent is a real state rather than a missing one: it means "whatever this
   * deployment defaults to", which is what every capture made before the picker existed says and
   * what a device that has never opened Settings keeps saying.
   *
   * Validated FAIL-CLOSED against the availability-gated catalog before anything is called — a
   * provider this deployment holds no key for is refused with the reason, never silently served by
   * the default. Naming a model here can therefore never widen what the gateway will do.
   */
  modelSelection: ModelSelectionSchema.optional(),
});

/**
 * `POST /api/multi/v1/models` — what can read a note here.
 *
 * A body with nothing in it, declared rather than skipped, so this route goes through the same
 * parse-then-handle path as every other and cannot become the one that accepts anything.
 */
export const MultiModelsRequestSchema = z.object({});
export type MultiModelsRequest = z.infer<typeof MultiModelsRequestSchema>;
export type MultiStructureRequest = z.infer<typeof MultiStructureRequestSchema>;

/**
 * Which structurer answered.
 *
 * `heuristic` is the deterministic on-server structurer that runs when no model credential is
 * present. It is named on every response — not only on `/health` — because this fleet has shipped
 * a stub that passed for a model before, and a per-response answer is the one a caller cannot fail
 * to read.
 */
export const MultiStructureEngineSchema = z.enum(['model', 'heuristic']);
export type MultiStructureEngine = z.infer<typeof MultiStructureEngineSchema>;

export const MultiStructureResponseSchema = z.object({
  structure: NoteStructureSchema,
  engine: MultiStructureEngineSchema,
});
export type MultiStructureResponse = z.infer<typeof MultiStructureResponseSchema>;

/**
 * The id of the task an ACTION becomes — `<captureId>#<index>`.
 *
 * Derived rather than minted, and that is the point: a task has to be tickable, and a tick is
 * stored against an id. A fresh ULID per action would be minted again on every render, so nothing
 * could stay ticked; the index into the note's own `actions` array is the only identifier that is
 * stable across reloads and across devices without storing a second record.
 *
 * The accepted cost is that re-structuring a note (`AF-MULTI69`) can renumber its actions, so a
 * tick could land on a different line. That is a real defect and it is unreachable today, because
 * nothing re-structures — it is called out on the row that would make it reachable.
 */
export function actionTaskId(captureId: string, index: number): string {
  return `${captureId}#${index}`;
}
